import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/Layout/AdminLayout';
import { Button, ConfirmationDialog, DataTable } from '@components/UI';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { trpc } from '@/api/trpc';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import type { ScreenerListItem } from './types/screenerEditor';

const ScreenerList = () => {
    const navigate = useNavigate();
    const [error, setError] = useState<string | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<ScreenerListItem | null>(null);

    const listQuery = useAppQuery(
        {
            ...trpc.screener.list.queryOptions()
        },
        {
            fallbackErrorMessage: 'Failed to load screeners',
            setError: (msg) => setError(msg)
        }
    );

    const rules = useMemo(() => listQuery.data?.rules ?? [], [listQuery.data?.rules]);

    const deleteMutation = useTrpcMutation(trpc.screener.delete.mutationOptions({}), {
        successMessage: 'Screener deleted',
        fallbackErrorMessage: 'Failed to delete screener'
    });

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return;

        try {
            await deleteMutation.mutateAsync({ screenerRuleId: deleteTarget.id });
            setDeleteTarget(null);
            void listQuery.refetch();
        } catch {
            // Toast handled by useTrpcMutation
        }
    };

    const handleCreateScreener = useCallback(() => {
        void navigate('/tools/screener/new');
    }, [navigate]);

    const columns: ColumnDef<ScreenerListItem>[] = useMemo(
        () => [
            {
                accessorKey: 'id',
                header: 'ID',
                minSize: 60,
                size: 80,
                maxSize: 100
            },
            {
                accessorKey: 'name',
                header: 'Name',
                minSize: 200,
                size: 350,
                maxSize: 500,
                cell: ({ row }) => (
                    <button
                        className="text-left text-blue-400 hover:text-blue-300 underline cursor-pointer"
                        type="button"
                        onClick={() => navigate(`/tools/screener/${row.original.id}`)}
                    >
                        {row.original.name}
                    </button>
                )
            },
            {
                accessorKey: 'description',
                header: 'Description',
                minSize: 200,
                size: 350,
                maxSize: 500,
                cell: ({ row }) => <span className="text-zinc-400 truncate block max-w-[400px]">{row.original.description ?? '—'}</span>
            },
            {
                accessorKey: 'isActive',
                header: 'Status',
                minSize: 80,
                size: 100,
                maxSize: 140,
                cell: ({ row }) => (
                    <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                            row.original.isActive
                                ? 'bg-green-900/50 text-green-300 border border-green-700'
                                : 'bg-zinc-700 text-zinc-400 border border-zinc-600'
                        }`}
                    >
                        {row.original.isActive ? 'Active' : 'Inactive'}
                    </span>
                )
            },
            {
                accessorKey: 'revision',
                header: 'Revision',
                minSize: 100,
                size: 130,
                maxSize: 160,
                cell: ({ row }) => {
                    const r = row.original;
                    return (
                        <span className="text-xs text-zinc-400 font-mono">
                            #{r.rootRuleId ?? r.id}.r{r.revision}
                        </span>
                    );
                }
            },
            {
                accessorKey: 'updatedAt',
                header: 'Updated',
                minSize: 140,
                size: 180,
                maxSize: 220
            },
            {
                id: 'actions',
                header: 'Actions',
                minSize: 180,
                size: 220,
                maxSize: 280,
                cell: ({ row }) => {
                    const r = row.original;
                    return (
                        <div className="flex gap-2">
                            <button
                                className="px-2 py-1 text-xs text-zinc-300 hover:text-white bg-zinc-800 hover:bg-zinc-700 rounded transition-colors"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    void navigate(`/tools/screener/${r.id}`);
                                }}
                            >
                                Edit
                            </button>
                            <button
                                className="px-2 py-1 text-xs text-zinc-300 hover:text-white bg-zinc-800 hover:bg-zinc-700 rounded transition-colors"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    void navigate(`/tools/screener/new?copyFrom=${r.id}`);
                                }}
                            >
                                Copy
                            </button>
                            <button
                                className="px-2 py-1 text-xs text-red-400 hover:text-red-300 bg-zinc-800 hover:bg-zinc-700 rounded transition-colors"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteTarget(r);
                                }}
                            >
                                Delete
                            </button>
                        </div>
                    );
                }
            }
        ],
        [navigate]
    );

    if (listQuery.isLoading) {
        return (
            <AdminLayout title="Screeners">
                <DataLoadingSpinner text="Loading screeners..." />
            </AdminLayout>
        );
    }

    if (error) {
        return (
            <AdminLayout title="Screeners">
                <div className="container mx-auto">
                    <div className="bg-red-100 text-red-800 border border-red-200 p-4 mb-4 rounded-md">{error}</div>
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout containerSize="1600px" title="Screeners">
            <div className="container mx-auto px-4">
                <div className="flex justify-between items-center mt-4">
                    <div>
                        <h3 className="text-white text-xl font-semibold mb-4">Screeners</h3>
                    </div>
                    <Button variant="primary" onClick={handleCreateScreener}>
                        Create Screener
                    </Button>
                </div>

                <DataTable allowOverflow striped columns={columns} data={rules} pagination={{ enabled: true, pageSize: 20 }} />

                <ConfirmationDialog
                    confirmText="Delete"
                    confirmVariant="danger"
                    isConfirmLoading={deleteMutation.isPending}
                    isOpen={deleteTarget !== null}
                    message={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
                    title="Delete Screener"
                    onCancel={() => setDeleteTarget(null)}
                    onConfirm={handleConfirmDelete}
                />
            </div>
        </AdminLayout>
    );
};

export default ScreenerList;
